// The open Penyedia paket project for everything inside its page: the paket
// home (project/Paket.tsx), the compact bar on its conversation route, and
// the proposal card Pluto puts in the chat (project/PaketProposalCard.tsx).
// Saving goes through here so the shared project list (sidebar, bar) and the
// paket home's header update at once.
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import * as API from "../api";
import type { PaketSaveInput, PaketSaveResult, Project } from "../api";
import type { ProjectsApi } from "../shell/Projects";

export interface PaketApi {
  project: Project;
  /** Saves the paket info; the list and the home's detail follow. */
  save: (input: PaketSaveInput) => Promise<PaketSaveResult>;
  /** Bumped on every save, so the home reloads its detail (checklist included). */
  version: number;
}

const PaketContext = createContext<PaketApi | null>(null);

export function usePaket(): PaketApi | null {
  return useContext(PaketContext);
}

export function PaketProvider({ project, projects, children }: { project: Project; projects: ProjectsApi; children: ReactNode }) {
  const [version, setVersion] = useState(0);
  const save = useCallback(
    async (input: PaketSaveInput) => {
      const result = await API.updatePaket(project.id, input);
      const rows = projects.list.data ?? [];
      projects.list.set(rows.map((r) => (r.id === result.project.id ? { ...r, ...result.project } : r)));
      setVersion((v) => v + 1);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project.id, projects.list.data],
  );
  const api = useMemo<PaketApi>(() => ({ project, save, version }), [project, save, version]);
  return <PaketContext.Provider value={api}>{children}</PaketContext.Provider>;
}

/** True while the paket has none of its info yet (the chat then offers the intake). */
export function paketIsEmpty(p: Pick<Project, "paketName" | "instansi" | "nilaiHps" | "deadline">): boolean {
  return !p.paketName && !p.instansi && p.nilaiHps == null && !p.deadline;
}
